import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createProductAdVideoSkill: Skill = {
    name: SKILL_NAMES.createProductAdVideo,
    description:
        'Short, high-energy product ad — 15-30 seconds of fast, punchy selling for a product or offer.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Product ad',
        blurb: 'Short, punchy ad built to sell a product or offer.',
        icon: 'sell',
        gradient: ['#A85F5F', '#B08F5A'],
    },
    instructions: `# Create Product Ad Video

The high-energy ad format: 15-30 seconds, fast cuts, punchy copy, a clear CTA.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill's pacing
rules deliberately override it where they conflict.

## Energy and pacing — the defining trait
- This is the one format where **short, frequent overlays are the goal**: cut
  often, each overlay lasting only as long as its short phrase takes to say. A
  calm one-overlay-per-sentence pace reads as sluggish and undercuts the format.
- Target 15-30 seconds total. If the script runs long, cut content — never slow
  the pace to fit it.
- Copy is short, confident, a little urgent — fragments beat full sentences.
  Punchiness comes from writing short beats, not trimming long ones: "Faster.
  Finally. Yours." is three overlays, each showing its word(s) in full.
- A deliberate one-word overlay is a style choice for this format, not a widow.
- Build toward a peak — hook, rising energy, payoff — don't let it stay flat.

## Structure
- **Hook** (first 2-3 seconds): something that stops the scroll — a question, a
  bold claim, an unexpected visual. Not the product name, not a generic opener.
- **Benefit beats**: a handful of short, concrete points, each showing the
  product/benefit in action rather than describing it. No vague hype.
- **Reveal/payoff**, then a **CTA close** ("Get yours today", "Try it free") —
  confident and direct, as the final beat.

## Media and visual style
- Kinetic: fast cuts, visible motion, close-ups, quick reveals — the cinematic
  quality bar still applies; energetic doesn't mean low-quality.
- Most beats can be media-driven — this format doesn't need a no-media setup beat.
  Reserve the clean bold-text treatment for the reveal and/or the CTA close, where
  a graphic moment hits harder than another clip.
- An ANIMATED_CAPTIONS-style design (word-by-word kinetic text) is a legitimate,
  often strong choice for a benefit beat or the reveal — one of the few formats
  where that's true. Use it deliberately for a beat or two, not everywhere.
- CTA/brand-name text that's a static UI element of the design is chrome, not
  transcript-bound; a transcript-driven overlay follows the spoken-words rule like
  everything else — check the design's asset types to tell which it is.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- Does it actually feel fast? Sparse or long-running beats are the failure mode
  here — add more, shorter beats rather than consolidating.
- Confirm a clear hook in the first seconds, a reveal that reads as the peak, and
  a direct CTA as the final beat — not a string of undifferentiated features.
- Confirm ANIMATED_CAPTIONS beats were deliberate accents, not the default.`,
};
